import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "../../../ui-components/molecules/tag/tag";
import { InboundMessageListItem } from "../../../app/data/communications/models";
import { TagColor } from "../../../app/data/common/tagColor";
import {
  PROCESSING_STATE_COLORS,
  formatProcessingStatusTag,
  formatRequestType,
} from "./constants";

const columnHelper = createColumnHelper<InboundMessageListItem>();

export const PICKUP_INBOX_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "receivedAt", label: "Received" },
  { id: "sender", label: "From" },
  { id: "subject", label: "Subject" },
  { id: "requestTypes", label: "Request types" },
  { id: "pendingReview", label: "Pending review" },
  { id: "attachmentCount", label: "Attachment count" },
  { id: "viewEmail", label: "View email" },
  { id: "processingState", label: "Processing Status" },
  { id: "id", label: "Message ID" },
  { id: "threadId", label: "Thread ID" },
  { id: "receiver", label: "To" },
  { id: "hasAttachments", label: "Has attachments" },
  { id: "hasPendingReview", label: "Has pending review" },
];

export const PICKUP_INBOX_COLUMNS_HIDDEN_BY_DEFAULT = [
  "id",
  "threadId",
  "receiver",
  "hasAttachments",
  "hasPendingReview",
  "processingState",
];

export type PickupInboxColumnsOptions = {
  onViewEmail: (row: InboundMessageListItem, e: React.MouseEvent) => void;
};

const getPickupInboxColumns = (options: PickupInboxColumnsOptions) => [
  columnHelper.accessor("receivedAt", {
    id: "receivedAt",
    header: "Received",
    cell: (info) => {
      const v = info.getValue();
      return v ? (v as string).toUiDateTimeFormat?.() ?? v : "—";
    },
    size: 190,
    minSize: 160,
  }),
  columnHelper.accessor("sender", {
    id: "sender",
    header: "From",
    size: 280,
    minSize: 200,
  }),
  columnHelper.accessor("subject", {
    id: "subject",
    header: "Subject",
    cell: (info) => info.getValue() || "—",
    size: 420,
    minSize: 280,
  }),
  columnHelper.accessor((row) => row.summary.requestTypes, {
    id: "requestTypes",
    header: "Request types",
    cell: (info) => {
      const types = info.getValue() || [];
      if (!types.length) return "—";
      return (
        <span className="xgs-pickup-inbox__request-types">
          {types.map((t) => (
            <Tag key={t} mods={{ color: TagColor.BLUE, uppercase: false }}>
              {formatRequestType(t)}
            </Tag>
          ))}
        </span>
      );
    },
    size: 140,
  }),
  columnHelper.accessor((row) => row.summary.pendingReviewCount, {
    id: "pendingReview",
    header: "Pending",
    cell: (info) => {
      const count = info.getValue();
      if (!count) return "—";
      return (
        <Tag mods={{ color: TagColor.ORANGE, uppercase: false }}>
          {count} Pending
        </Tag>
      );
    },
    size: 110,
  }),
  columnHelper.accessor("attachmentCount", {
    id: "attachmentCount",
    header: "Attachment count",
    cell: (info) => info.getValue() ?? 0,
    size: 120,
  }),
  columnHelper.display({
    id: "viewEmail",
    header: "View email",
    cell: (info) => (
      <button
        type="button"
        className="xgs-pickup-inbox__view-email-link"
        onClick={(e) => options.onViewEmail(info.row.original, e)}
      >
        View email
      </button>
    ),
    size: 100,
  }),
  columnHelper.accessor("processingState", {
    id: "processingState",
    header: "Processing Status",
    cell: (info) => {
      const state = info.getValue();
      const label = formatProcessingStatusTag(state);
      if (!label) return "—";
      return (
        <Tag mods={{ color: PROCESSING_STATE_COLORS[state], uppercase: false }}>
          {label}
        </Tag>
      );
    },
    size: 160,
  }),
  columnHelper.accessor("id", {
    id: "id",
    header: "Message ID",
    size: 100,
  }),
  columnHelper.accessor("threadId", {
    id: "threadId",
    header: "Thread ID",
    cell: (info) => info.getValue() ?? "—",
    size: 90,
  }),
  columnHelper.accessor("receiver", {
    id: "receiver",
    header: "To",
    cell: (info) => info.getValue() || "—",
    size: 180,
  }),
  columnHelper.accessor("hasAttachments", {
    id: "hasAttachments",
    header: "Attachments",
    cell: (info) => (info.getValue() ? "Yes" : "No"),
    size: 100,
  }),
  columnHelper.accessor((row) => row.summary.hasPendingReview, {
    id: "hasPendingReview",
    header: "Needs review",
    cell: (info) => (info.getValue() ? "Yes" : "No"),
    size: 100,
  }),
];

export default getPickupInboxColumns;
